import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TrelloCardDetail } from '../../entities/trello-card';
import { getTrelloCardDetail } from '../../shared/api/dashboard';
import { contrastTextColor } from '../../shared/utils/color';
import { TaskDetailShell } from '../../widgets/task-detail/TaskDetailShell';

const TrelloTaskDetailPage: React.FC = () => {
  const { projectId, cardId } = useParams<{ projectId: string; cardId: string }>();

  const [card, setCard] = useState<TrelloCardDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!cardId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getTrelloCardDetail(cardId)
      .then(({ card: c }) => {
        if (!cancelled) setCard(c);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load this card.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cardId]);

  const isOverdue = !!card?.due && !card.closed && new Date(card.due) < new Date();

  return (
    <TaskDetailShell
      backTo={`/dashboard/projects/${projectId}`}
      backLabel="Back to board"
      provider="trello"
      titleKey={cardId}
      isLoading={isLoading}
      error={error}
      externalUrl={card?.url}
      externalLabel="Open in Trello"
    >
      {card && (
        <>
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-5 gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-lg font-semibold">{card.name}</span>
                {card.closed && <span className="badge badge-ghost">Archived</span>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {card.due && (
                  <span className={`badge badge-outline ${isOverdue ? 'text-error border-error' : ''}`}>
                    Due {new Date(card.due).toLocaleDateString()}
                  </span>
                )}
                {card.labels.map((l) => (
                  <span
                    key={l.id}
                    className="badge border-0"
                    style={{ backgroundColor: l.color, color: contrastTextColor(l.color) }}
                  >
                    {l.name}
                  </span>
                ))}
              </div>
              {card.description ? (
                <div className="text-sm whitespace-pre-wrap pt-1">{card.description}</div>
              ) : (
                <div className="text-sm opacity-50 pt-1">No description.</div>
              )}
            </div>
          </div>

          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-5 gap-3">
              <div className="font-semibold">Comments</div>
              {card.comments.length > 0 ? (
                <div className="space-y-1.5">
                  {card.comments.map((c) => (
                    <div key={c.id} className="rounded-lg bg-base-200 px-3 py-2 text-sm space-y-1">
                      <div className="text-xs opacity-40">{new Date(c.date).toLocaleString()}</div>
                      <div className="whitespace-pre-wrap">{c.text}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm opacity-50">No comments.</div>
              )}
            </div>
          </div>
        </>
      )}
    </TaskDetailShell>
  );
};

export default TrelloTaskDetailPage;
